import React, { useState } from 'react';
import { ShoppingCart, Coins, Star, Gift, Award, Smartphone, Headphones, Coffee, Car, Gamepad2, Watch, Shirt } from 'lucide-react';

const Store = () => {
  const [userPoints, setUserPoints] = useState(2450);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);

  // Catégories
  const categories = [
    { id: 'all', name: 'Tout', icon: ShoppingCart },
    { id: 'tech', name: 'High-Tech', icon: Smartphone },
    { id: 'lifestyle', name: 'Lifestyle', icon: Coffee },
    { id: 'rewards', name: 'Récompenses', icon: Award },
    { id: 'vouchers', name: 'Bons d\'achat', icon: Gift }
  ];

  // Produits du store
  const products = [
    {
      id: 1,
      name: 'iPhone 15 Pro',
      description: 'Smartphone Apple dernière génération',
      points: 15000,
      category: 'tech',
      image: '📱',
      popular: true,
      stock: 3
    },
    {
      id: 2,
      name: 'AirPods Pro',
      description: 'Écouteurs sans fil avec réduction de bruit',
      points: 3500,
      category: 'tech',
      image: '🎧',
      popular: true,
      stock: 8
    },
    {
      id: 3,
      name: 'MacBook Air M3',
      description: 'Ordinateur portable ultra-performant',
      points: 25000,
      category: 'tech',
      image: '💻',
      popular: false,
      stock: 2
    },
    {
      id: 4,
      name: 'Apple Watch Series 9',
      description: 'Montre connectée avec suivi santé',
      points: 8500,
      category: 'tech',
      image: '⌚',
      popular: true,
      stock: 5
    },
    {
      id: 5,
      name: 'Bon Amazon 100€',
      description: 'Carte cadeau Amazon utilisable partout',
      points: 2000,
      category: 'vouchers',
      image: '🎁',
      popular: true,
      stock: 50
    },
    {
      id: 6,
      name: 'Nespresso Vertuo',
      description: 'Machine à café haut de gamme',
      points: 4500,
      category: 'lifestyle',
      image: '☕',
      popular: false,
      stock: 6
    },
    {
      id: 7,
      name: 'PlayStation 5',
      description: 'Console de jeux nouvelle génération',
      points: 12000,
      category: 'tech',
      image: '🎮',
      popular: true,
      stock: 1
    },
    {
      id: 8,
      name: 'Polo Eco\'enr',
      description: 'Polo brodé aux couleurs de l\'entreprise',
      points: 800,
      category: 'lifestyle',
      image: '👕',
      popular: false,
      stock: 25
    },
    {
      id: 9,
      name: 'Journée congé bonus',
      description: 'Une journée de congé supplémentaire',
      points: 5000,
      category: 'rewards',
      image: '🏖️',
      popular: true,
      stock: 12
    },
    {
      id: 10,
      name: 'Parking VIP 1 mois',
      description: 'Place de parking réservée pendant 1 mois',
      points: 1500,
      category: 'rewards',
      image: '🚗',
      popular: false,
      stock: 4
    }
  ];

  // Filtrer les produits par catégorie
  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => product.category === selectedCategory);

  // Ajouter au panier
  const handleAddToCart = (product) => {
    if (userPoints >= product.points) {
      setCart([...cart, product]);
      setUserPoints(userPoints - product.points);
      console.log(`${product.name} ajouté au panier !`);
    } else {
      console.log('Points insuffisants pour cet article !');
    }
  };

  // Vider le panier
  const clearCart = () => {
    // Rembourser les points
    const totalPoints = cart.reduce((sum, item) => sum + item.points, 0);
    setUserPoints(userPoints + totalPoints);
    setCart([]);
  };

  const getStockColor = (stock) => {
    if (stock <= 2) return 'text-red-600';
    if (stock <= 5) return 'text-orange-600';
    return 'text-green-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Store Eco'enr</h1>
          <p className="text-gray-600 mt-1">Échangez vos points contre des récompenses exclusives</p>
        </div>
        <div className="flex items-center gap-6">
          {/* Points disponibles */}
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-xl px-6 py-3 text-white">
            <div className="flex items-center gap-2">
              <Coins className="w-6 h-6" />
              <div>
                <p className="text-sm font-medium">Mes points</p>
                <p className="text-2xl font-bold">{userPoints.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          {/* Panier */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3">
            <div className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-gray-600" />
              <span className="font-medium text-gray-900">{cart.length} article(s)</span>
              {cart.length > 0 && (
                <button 
                  onClick={clearCart}
                  className="ml-2 text-sm text-red-600 hover:text-red-800"
                >
                  Vider
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Informations sur les points */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Star className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-900 mb-1">Comment gagner des points ?</h3>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Terminer des challenges : jusqu'à 500 points</li>
              <li>• Atteindre vos objectifs mensuels : 1000 points</li>
              <li>• Parrainer un nouveau client : 200 points</li>
              <li>• Participer aux événements d'équipe : 100 points</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Filtres par catégorie */}
      <div className="flex items-center gap-4 overflow-x-auto pb-2">
        {categories.map((category) => {
          const Icon = category.icon;
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary text-white'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {category.name}
            </button>
          );
        })}
      </div>

      {/* Grille des produits */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredProducts.map((product) => (
          <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
            {/* Badge populaire */}
            {product.popular && (
              <div className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-xs font-bold px-2 py-1 absolute z-10 rounded-br-lg">
                POPULAIRE
              </div>
            )}
            
            {/* Image du produit */}
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 h-48 flex items-center justify-center text-6xl relative">
              {product.image}
              {product.stock <= 2 && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                  Stock faible
                </div>
              )}
            </div>

            {/* Informations produit */}
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{product.description}</p>
              
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1">
                  <Coins className="w-4 h-4 text-yellow-500" />
                  <span className="font-bold text-lg text-gray-900">
                    {product.points.toLocaleString()}
                  </span>
                </div>
                <span className={`text-sm font-medium ${getStockColor(product.stock)}`}>
                  Stock: {product.stock}
                </span>
              </div>

              <button
                onClick={() => handleAddToCart(product)}
                disabled={userPoints < product.points || product.stock === 0}
                className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                  userPoints >= product.points && product.stock > 0
                    ? 'bg-primary text-white hover:bg-primary/90'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                {product.stock === 0 ? 'Rupture de stock' : 
                 userPoints < product.points ? 'Points insuffisants' : 
                 'Échanger'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Message si aucun produit */}
      {filteredProducts.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun produit trouvé</h3>
          <p className="text-gray-600">Essayez une autre catégorie</p>
        </div>
      )}
    </div>
  );
};

export default Store;